import {
  HorizontalLine,
  VerticalLine,
} from '@/reports/property/sections/components';
import { propertiesTypes, serializedTypes } from '@/reports/types';
import { Stack, Box, Icons, Text, styled } from '@/theme/index';
import {
  calculateMarketPricesRange,
  formatMarket,
  lastMarketValueUpdated,
} from '@/utils/marketValues/marketValues';
import {
  lastRentalValue,
  formatRental,
} from '@/utils/rentalValues/rentalValues';

const StyledText = styled(Text)(({ theme }) => ({
  color: theme.palette.customCard?.contrastText || theme.palette.grey[600],
}));

export interface EstimatedSummary {
  /** MarketValues array to use on the summary */
  marketValues: propertiesTypes.MarketValues;
  /** Rental Values array to use on the summary */
  rentalValues: propertiesTypes.RentalValues;
  /** Property object used on the property report */
  property: serializedTypes.SerializedProperty;
}

const AVM_PARTNERS_ARRAY = ['bk', 'ca', 'arch'];

export function EstimatedSummary({
  marketValues,
  rentalValues,
  property,
}: EstimatedSummary) {
  const marketRange = calculateMarketPricesRange(
    marketValues,
    AVM_PARTNERS_ARRAY
  );
  const lastEditedMarket = lastMarketValueUpdated(marketValues);
  const marketInfo = formatMarket(lastEditedMarket, marketRange, property);

  const recentRental = lastRentalValue(rentalValues);
  const rentalInfo = formatRental(recentRental);

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center">
      <Box textAlign={{ xs: 'left', sm: 'center' }} flex="1">
        <Box textAlign="left" display="inline-table">
          <Text variant="h5" mb={1.25}>
            Est. Market Value Range
          </Text>
          <Box>
            <Text variant="h2" mb={0}>
              {marketInfo.range}
            </Text>
            <StyledText variant="body2">
              Last updated {marketInfo.date}
            </StyledText>
          </Box>
          <Stack direction="row" mt={2.5} mr={0.625} alignItems="center">
            <Icons.Paid
              color="error"
              sx={{ verticalAlign: 'bottom', fontSize: '1rem' }}
            />
            <Text variant="body2" fontWeight="bold" ml={0.625}>
              Last Sold {marketInfo.price}
            </Text>
            {marketInfo.price !== '-' && marketInfo.price && (
              <Text variant="body2" ml={0.375}>
                ({marketInfo.soldDate})
              </Text>
            )}
          </Stack>
        </Box>
      </Box>
      <Box display={{ xs: 'block', sm: 'none' }}>
        <HorizontalLine sx={{ margin: '30px auto' }} />
      </Box>
      <Box display={{ xs: 'none', sm: 'block' }}>
        <VerticalLine sx={{ height: '100%', margin: 'auto 10px' }} />
      </Box>
      <Box textAlign={{ xs: 'left', sm: 'center' }} flex="1">
        <Box textAlign="left" display="inline-table">
          <Text variant="h5" mb={1.25}>
            Est. Monthly Rent
          </Text>
          <Box>
            <Text variant="h2" mb={0}>
              {rentalInfo.range}
            </Text>
            <StyledText variant="body2">
              Last updated {rentalInfo.date}
            </StyledText>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
